import { api } from './api';
import type { Tone } from './clients';
import type { SessionItem, SessionStatus } from './types';

/**
 * Sessions endpoints and presentation helpers, mirroring lib/clients.ts.
 * Tone maps live here so `lib/` never imports from `design/`; formatters use
 * toLocale* because there is no date library in this project and one isn't
 * worth adding (recorded house decision in lib/clients.ts).
 */

export const sessionsApi = {
  list: () => api.get<{ sessions: SessionItem[]; bookingLink: string | null }>('/sessions'),
};

export function sessionStatusTone(status: SessionStatus): Tone {
  if (status === 'confirmed') return 'success';
  if (status === 'pending_payment') return 'warning';
  if (status === 'cancelled') return 'danger';
  return 'neutral';
}

export function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** "Mon 18 Aug · 2:00 PM", year appended only when it isn't the current one. */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const day = d.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return `${day} · ${formatTime(iso)}`;
}

export function formatDuration(startsAt: string, endsAt: string | null): string {
  if (!endsAt) return '—';
  const minutes = Math.round((new Date(endsAt).getTime() - new Date(startsAt).getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes <= 0) return '—';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Null in, null out — a session without a price renders no price at all. */
export function formatPrice(amountCents: number | null, currency: string): string | null {
  if (amountCents === null) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(
      amountCents / 100,
    );
  } catch {
    return `$${(amountCents / 100).toFixed(2)}`;
  }
}

export function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/** Monday-started local week. */
export function isThisWeek(iso: string): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const nextMonday = new Date(monday);
  nextMonday.setDate(monday.getDate() + 7);
  return d >= monday && d < nextMonday;
}
